// Tab "Cobranças" no detalhe do cliente (Story 4.10 AC5).
// Reusa charge-display + useCharges com filtro clientId.

import { Banknote } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatBR, parseLocalDate } from '@control-center/shared';

import { cn } from '@control-center/ui';

import {
  CHARGE_STATUS_DOT,
  CHARGE_STATUS_LABEL,
  chargeTypeLabel,
  formatAmountBR,
} from './charge-display';
import { useCharges } from '../../hooks/use-charges';

interface ClientChargesTabProps {
  clientId: string;
}

export function ClientChargesTab({ clientId }: ClientChargesTabProps): JSX.Element {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const listQ = useCharges({ scope: 'todas', clientId, page, pageSize });

  if (listQ.isPending) {
    return <div className="py-8 text-center text-[13px] text-muted">Carregando…</div>;
  }

  const items = listQ.data?.data ?? [];
  const total = listQ.data?.pagination.total ?? 0;

  if (items.length === 0) {
    return (
      <div className="rounded-[10px] border border-line bg-panel-2 p-12 text-center">
        <Banknote size={24} strokeWidth={1.5} className="mx-auto mb-3 text-muted-3" />
        <p className="text-[13px] text-muted">Nenhuma cobrança registrada pra este cliente.</p>
      </div>
    );
  }

  return (
    <div>
      <p className="mb-3 text-[12px] text-muted">
        {total} {total === 1 ? 'cobrança' : 'cobranças'} no total
      </p>
      <div className="overflow-hidden rounded-[10px] border border-line bg-panel">
        <table className="w-full text-[13px]">
          <thead className="border-b border-line bg-panel-2 text-[11px] uppercase tracking-wider text-muted-3">
            <tr>
              <th className="px-4 py-3 text-left">Criada</th>
              <th className="px-4 py-3 text-left">Descrição</th>
              <th className="px-4 py-3 text-left">Tipo</th>
              <th className="px-4 py-3 text-right">Valor</th>
              <th className="px-4 py-3 text-left">Vencimento</th>
              <th className="px-4 py-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr
                key={c.id}
                onClick={() => navigate(`/charges/${c.id}`)}
                className="cursor-pointer border-b border-line last:border-0 hover:bg-panel-2"
              >
                <td className="px-4 py-3 text-muted tnum">{formatBR(new Date(c.createdAt))}</td>
                <td className="px-4 py-3 text-ink">{c.description}</td>
                <td className="px-4 py-3 text-muted">{chargeTypeLabel(c)}</td>
                <td className="px-4 py-3 text-right tnum text-ink">{formatAmountBR(c.amount)}</td>
                <td className="px-4 py-3 text-muted tnum">{formatBR(parseLocalDate(c.dueDate))}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-[6px]">
                    <span
                      aria-hidden
                      className={cn(
                        'inline-block h-[8px] w-[8px] rounded-full ring-2',
                        CHARGE_STATUS_DOT[c.status] ?? 'bg-muted-2 ring-muted-3/15',
                      )}
                    />
                    <span className="text-muted">{CHARGE_STATUS_LABEL[c.status] ?? c.status}</span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > pageSize && (
        <div className="mt-3 flex items-center justify-end gap-2 text-[11px] text-muted">
          <span>
            Página {page} de {Math.ceil(total / pageSize)}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-[6px] border border-line px-2 py-1 disabled:opacity-50"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={page * pageSize >= total}
            className="rounded-[6px] border border-line px-2 py-1 disabled:opacity-50"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
